import {Connection, Edge} from 'reactflow';

import {assert} from 'Asserts';
import {
  ProcessExecutionType,
  ProcessFlowchartNodeDetailsType,
  ProcessSingletonTaskSchemaName,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  ProcessFlowchartForValidation,
  ProcessFlowchartValidationError,
} from 'processes/graph-builder-processes/validation/ProcessFlowchartForValidationTypes';
import {ProcessFlowchartDesignSpace} from 'processes/graph-builder-processes/validation/ProcessGraphBuilderConstants';
import {
  ProcessTaskDefinitionVersionGraphQL,
  ProcessFlowchartNodeType,
  SidepanelTab,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';
import {
  FlowchartNode,
  TaskDefinitionVersionSpecWithTaskSchema,
  TaskSchemaNodeSectionData,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorNodeTypes';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {SidepanelSize} from 'ui-library/reactflow/sidepanels/FlowchartContextPanel';

import {ProcessTaskSchemaWithoutFlowchartConfigGraphQL} from '../../../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaWithoutFlowchartConfigGraphQL';

/**
 * Whether the node sidepanel can be opened via a single click or a double click.
 * With double click, the node will have an edit/view icon in the node toolbar.
 */
export enum FlowchartNodeEditMode {
  SINGLE_CLICK = 'single_click',
  DOUBLE_CLICK = 'double_click',
}

/**
 * Context data needed for creating new nodes
 */
export interface NodeCreationContext {
  // TODO: Determine if this is sufficient for both workflows + procedures
  // Workflows will use this to extract some information for new schemas (ex: org)
  // Procedures will need the unit op singleton, but this will be a procedures singleton task schema...
  // Expecting this context to evolve in future PRs
  rootTaskSchema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL;
  singletonTaskSchemaForExecutionType: ProcessTaskSchemaWithoutFlowchartConfigGraphQL | null;
  existingNodeNames?: Array<string>;
}

export const DEFAULT_MAX_EDGES = 1000;

abstract class ProcessFlowchartNodeAdapterBlockingRules {
  /**
   * Returns whether a node is deletable within the flowchart canvas.
   * Override this method to prevent deletion of specific node types.
   */
  abstract isDeletable(): boolean;

  /**
   * Returns the maximum number of edges that can be drawn to a node of this type.
   */
  abstract maxIncomingEdges(isFlowchartOutput?: boolean): number;

  /**
   * Returns the maximum number of edges that can be drawn from a node of this type.
   */
  abstract maxOutgoingEdges(isFlowchartOutput?: boolean): number;

  /**
   * Returns the node types that cannot be adjacent to this node type.
   * This is used to prevent invalid connections between nodes.
   */
  abstract blockedAdjacentNodeTypes(): Array<ProcessFlowchartNodeType>;
}

/**
 * Base type for adapters that extend the processes editor with a new node type.
 */
export abstract class ProcessFlowchartNodeAdapter extends ProcessFlowchartNodeAdapterBlockingRules {
  /**
   * The type of nodes that this adapter can handle.
   */
  abstract readonly nodeType: ProcessFlowchartNodeType;

  abstract validateFlowchartForNodeType(
    flowchart: ProcessFlowchartForValidation,
    designSpace: ProcessFlowchartDesignSpace,
    didInitialFlowchartHaveChainedRouters: boolean,
    disconnectedNodeIds?: Array<string>
  ): Array<ProcessFlowchartValidationError>;

  abstract isConnectionValid?(connection: Connection, existingEdges: Array<Edge>): boolean;
}

export abstract class DefaultProcessFlowchartNodeAdapter extends ProcessFlowchartNodeAdapter {
  /** Override to prevent deletion of specific node types. */
  isDeletable(): boolean {
    return true;
  }

  /** Override to change the maximum number of incoming edges for this node type. */
  maxIncomingEdges(isFlowchartOutput?: boolean): number {
    return DEFAULT_MAX_EDGES;
  }

  /** Override to change the maximum number of outgoing edges for this node type. */
  maxOutgoingEdges(isFlowchartOutput?: boolean): number {
    return DEFAULT_MAX_EDGES;
  }

  /** Override to prevent connections between this node type and specific adjacent node types. */
  blockedAdjacentNodeTypes(): Array<ProcessFlowchartNodeType> {
    return [];
  }

  /** Override to add custom connection validation logic. */
  isConnectionValid?(connection: Connection, existingEdges: Array<Edge>): boolean {
    return true;
  }

  /** Override to display content in the node details sidepanel when this node is selected. */
  nodeDetailsSidepanelContent(_selectedNode: FlowchartNode): JSX.Element | null {
    return null;
  }
}

export interface TaskSchemaDisplayOptions {
  rightSidepanelSize?: SidepanelSize;
  rightSidepanelNameOverride?: (nodeName: string, selectedFieldName: string | null) => string;
}

/**
 * Base type for adapters that extend the processes editor with a new task execution type.
 *
 * When extending the processes system with a new execution type, application teams provide a subtype
 * of this class to tell the editor how to render and work with the new type. Each adapter
 * is specific to one task execution type, as defined by the `executionType` associated with the node.
 *
 * Node adapters are provided to the editor via plugins. See ProcessesPluginBase for more
 * information.
 *
 * See this confluence page for more information on plugins: https://benchling.atlassian.net/wiki/spaces/Workflows/pages/1285619729/Adding+A+Processes+Plugin
 */
export abstract class ProcessFlowchartTaskSchemaNodeAdapter<
  TFrontendApplicationData,
  TNetworkApplicationData,
> extends ProcessFlowchartNodeAdapterBlockingRules {
  nodeType: ProcessFlowchartNodeType = ProcessFlowchartNodeType.TASK_SCHEMA_NODE;
  /**
   * The execution type for tasks of nodes that this adapter can handle.
   */
  abstract readonly executionType: ProcessExecutionType;

  /**
   * The display name for the node type, used for error messages.
   */
  abstract readonly typeDisplayName: string;

  /**
   * Whether the node sidepanel can be opened via a single click or a double click.
   */
  abstract readonly editMode: FlowchartNodeEditMode;

  /**
   * Whether the node name can be edited via double-click directly on the canvas.
   * When true, double-clicking the node name will allow inline editing.
   * When false (default), the node name can only be edited via the sidepanel.
   *
   * When overriding to true, the adapter must implement `onUpdateNodeName`
   * to handle updating the underlying model data with the new name.
   */
  readonly isNodeNameDoubleClickEditable: boolean = false;

  /**
   * Updates the task definition version spec with a new node name.
   * This is called when the user edits the node name via double-click on the canvas.
   * Only called when `isNodeNameDoubleClickEditable` is true.
   *
   * @param currentSpec - The current task definition version spec to update.
   * @param newName - The new name for the node.
   * @returns The updated task definition version spec with the new name set in the application data,
   *          or null if this adapter does not support name editing.
   */
  onUpdateNodeName(
    currentSpec: TaskDefinitionVersionSpecWithTaskSchema<TFrontendApplicationData>,
    newName: string
  ): TaskDefinitionVersionSpecWithTaskSchema<TFrontendApplicationData> | null {
    assert(
      this.isNodeNameDoubleClickEditable,
      'Node name double click editing is not enabled for procedure method nodes'
    );
    return null;
  }

  /**
   * Convert a task definition version from the GQL type to the frontend application data type.
   * The frontend application data type should be the desired appliciation format for interacting with
   * the node's sidepanel content, etc.
   *
   * @param taskDefinitionVersionGQL - The task definition version GQL to convert.
   * @returns The converted task definition version with frontend application data.
   */
  abstract convertTaskDefinitionVersionGQLToApplicationData(
    taskDefinitionVersionGQL: ProcessTaskDefinitionVersionGraphQL
  ): TFrontendApplicationData;

  /**
   * Convert a task definition version from the frontend application data type to the network application data type.
   * The network application data type should be the desired format for the backend network spec.
   *
   * @param taskDefinitionVersionSpec - The task definition version spec to convert.
   * @returns The converted task definition version with network application data.
   */
  abstract convertTaskDefinitionVersionSpecToNetworkApplicationData(
    taskDefinitionVersionSpec: TaskDefinitionVersionSpecWithTaskSchema<TFrontendApplicationData>,
    flowchartConfigId: string
  ): TNetworkApplicationData;

  /**
   * Provides a subtitle to use when rendering the node on the flowchart canvas.
   *
   * @param taskDefinitionVersion - The task definition version for the node being rendered.
   * @param isFlowchartOutput - If this adapter is for a node associated with the flowchart execution type,
   * this flag signals if the start or output node is being rendered.
   * @returns The subtitle, or null if no subtitle should be displayed.
   */
  abstract getNodeSubtitle(
    taskDefinitionVersion: TaskDefinitionVersionSpecWithTaskSchema<TFrontendApplicationData>,
    isFlowchartOutput?: boolean
  ): string | null;

  /**
   * Provides an icon to use when rendering the node on the flowchart canvas.
   *
   * @param taskDefinitionVersion - The task definition version for the node being rendered.
   * @param isFlowchartOutput - If this adapter is for a node associated with the flowchart execution type,
   * this flag signals if the start or output node is being rendered.
   * @returns The icon name, or null if no icon should be displayed.
   */
  abstract getNodeIcon(
    taskDefinitionVersion: TaskDefinitionVersionSpecWithTaskSchema<TFrontendApplicationData>,
    isFlowchartOutput?: boolean
  ): BenchlingIconName | null;

  /**
   * Creates the sections of input and output parameters to use when rendering the node on the
   * flowchart canvas.
   *
   * Each section provides a custom grouping of the node's input and output parameters to use when
   * displaying the node. Application teams can use this to group parameters into logical sections
   * based on their functionality.
   *
   * @param taskDefinitionVersion - The task definition version for the node being rendered.
   * @param nodeDetailsType - The type of node being rendered.
   * @returns The sections to display on the node on the canvas.
   */
  abstract createSections(
    taskDefinitionVersion: TaskDefinitionVersionSpecWithTaskSchema<TFrontendApplicationData>,
    nodeDetailsType: ProcessFlowchartNodeDetailsType
  ): Array<TaskSchemaNodeSectionData>;

  abstract nodeDetailsSidepanelContent: Array<SidepanelTab>;

  /**
   * Optionally update a task definition version spec using its application data.
   *
   * Some execution types store their effective field definitions inside application data and do not
   * receive updated task definition version field definitions from the backend until persistence.
   * Adapters can override this to derive field definitions, and potentially other properties, from application data so that flowchart validation stays accurate.
   *
   * PTDVs use field definitions, but some domains (e.g., Procedures) use schema fields.
   * Adapters must convert by overriding this method.
   */
  updateTaskDefinitionVersionWithApplicationData(
    taskDefinitionVersion: TaskDefinitionVersionSpecWithTaskSchema<TFrontendApplicationData>
  ): TaskDefinitionVersionSpecWithTaskSchema<TFrontendApplicationData> {
    return taskDefinitionVersion;
  }

  /**
   * Create a new node in the flowchart, of the type this adapter handles.
   *
   * @param context Context data needed for node creation
   * @returns A Promise that resolves to the TaskDefinitionVersionSpec of the new node to update the flowchart with,
   * or null if the user cancels creation. Allows for modal dialogs to be shown to the user before creation as needed.
   */
  abstract onCreateNewNode(
    context: NodeCreationContext
  ): Promise<TaskDefinitionVersionSpecWithTaskSchema<TFrontendApplicationData> | null>;

  /**
   * Get the details of a new node to be created.
   *
   * @param context Context data needed for node creation
   * @returns The details of the new node to be created. Subtitle and icon are optional.
   */
  abstract getNewNodeDetails(context: NodeCreationContext): {
    nodeName: string;
    nodeSubtitle: string | null;
    nodeIcon: BenchlingIconName | null;
  };

  /**
   * Provides the name of the singleton task schema to use when creating a new node.
   * If null, will create a new task schema of the execution type for the given adapter.
   *
   * @returns The name of the singleton task schema to use when creating a new node.
   */
  abstract getSingletonTaskSchemaName(
    rootTaskSchema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL
  ): ProcessSingletonTaskSchemaName | null;

  validateFlowchartForNodeType(
    flowchart: ProcessFlowchartForValidation,
    designSpace: ProcessFlowchartDesignSpace,
    didInitialFlowchartHaveChainedRouters: boolean,
    disconnectedNodeIds?: Array<string>
  ): Array<ProcessFlowchartValidationError> {
    return [];
  }

  /**
   * Provides a name to use when rendering the node on the flowchart canvas.
   * This is default implementation that can be overridden by adapters.
   *
   * Handles incrementing based on existing nodes with the same base name.
   * For example, if "Task Schema 1" exists, the next one will be "Task Schema 2".
   *
   * @param taskDefinitionVersion - The task definition version for the node.
   * @param isFlowchartOutput - If adapter is for a node associated with the flowchart execution type,
   * this flag signals if this node is the start or end node.
   * @param existingNodeNames - Array of existing node names on the canvas for incrementing logic
   * @returns The default name for the node.
   */
  abstract getNodeName(
    taskDefinitionVersion: TaskDefinitionVersionSpecWithTaskSchema<TFrontendApplicationData>,
    isFlowchartOutput?: boolean,
    existingNodeNames?: Array<string>
  ): string;

  /**
   * Get the field name for a given field ID by searching through the node's sections.
   * This includes dynamically added sections like "Map from Start".
   *
   * @param node - The flowchart node to search within.
   * @param fieldId - The client ID of the field to find.
   * @returns The field name, or null if not found.
   */
  getFieldNameFromFieldId(node: FlowchartNode, fieldId: string): string | null {
    // Default implementation - override in subclasses if needed
    return null;
  }

  getDisplayOptions(): TaskSchemaDisplayOptions {
    return {
      rightSidepanelSize: SidepanelSize.MEDIUM,
      rightSidepanelNameOverride: (nodeName: string, _selectedFieldName: string | null) =>
        `Node ${nodeName} Details`,
    };
  }

  /**
   * Optional: Add custom connection validation rules for this node type.
   * This is called AFTER default validation passes.
   *
   * @param connection - The connection being attempted
   * @param existingEdges - Array of existing edges to check for duplicates
   * @returns true if connection should be allowed, false to reject
   */
  isConnectionValid?(connection: Connection, existingEdges: Array<Edge>): boolean;

  isDeletable(): boolean {
    return true;
  }

  maxIncomingEdges(isFlowchartOutput?: boolean): number {
    return DEFAULT_MAX_EDGES;
  }

  maxOutgoingEdges(isFlowchartOutput?: boolean): number {
    return DEFAULT_MAX_EDGES;
  }

  blockedAdjacentNodeTypes(): Array<ProcessFlowchartNodeType> {
    return [ProcessFlowchartNodeType.STOP_NODE];
  }
}
